import {DateTime} from 'luxon';

/**
 * Converts a UTC timestamp (in milliseconds) to local time by subtracting the local timezone offset.
 *
 * @param timestamp - A timestamp in UTC (milliseconds since epoch)
 * @returns The timestamp adjusted to local time, or `null` if input is `null`
 */
export function shiftUTCTimestampToLocal(timestamp: number | null): number | null {

    return timestamp
        ? timestamp - DateTime.local().offset * 60 * 1000
        : timestamp;
}

/**
 * Converts a local timestamp (in milliseconds) to UTC by adding the local timezone offset at that time.
 *
 * @param timestamp - A timestamp in local time (milliseconds since epoch)
 * @returns The timestamp adjusted to UTC, or `null` if input is `null`
 */
export function shiftLocalTimestampToUTC(timestamp: number | null): number | null {

    return timestamp
        ? timestamp + DateTime.fromMillis(timestamp).offset * 60 * 1000
        : timestamp;
}

/**
 * Normalizes a date-like value to epoch milliseconds.
 *
 * Accepts a timestamp in seconds or milliseconds (number or numeric string),
 * or an ISO string (with time, or date-only "YYYY-MM-DD"). Seconds-based
 * timestamps are orders of magnitude smaller than millisecond ones, so any
 * finite value below 1e12 is treated as seconds and scaled up.
 *
 * @param value - A timestamp (seconds/ms) or ISO date string
 * @returns Epoch milliseconds, or `null` if the value is empty or unparseable
 */
export function toMillis(value: number | string | null | undefined): number | null {

    if ("undefined" === typeof value || null === value || "" === value) {

        return null;
    }

    const timestamp: number = Number(value);

    if (Number.isFinite(timestamp)) {

        return timestamp < 1e12 ? timestamp * 1000 : timestamp;
    }

    const date = DateTime.fromISO(value as string);

    return date.isValid ? date.toMillis() : null;
}